import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { ArrowLeft, Database, RefreshCw, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { useApi } from "@/api";
import {
  type SourceOut,
  FrequencySelect,
  containerRefLabel,
  sourceStatusTone,
} from "@/components/manage/connectors";
import { EmptyRow, Section } from "@/components/manage/Section";
import { PageHeader } from "@/components/manage/PageHeader";
import { StatStrip } from "@/components/manage/StatStrip";
import { StatusBadge } from "@/components/manage/StatusBadge";
import { useIsDemoManage } from "@/components/manage/org-context";

export const Route = createFileRoute("/manage/datasets/$id/")({
  component: DatasetDetailPage,
});

interface DatasetRead {
  id: string;
  org_id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  created_at: string;
  updated_at: string;
}

type StageState = "done" | "active" | "pending" | "failed";

interface FileProgress {
  pct: number;
  label: string;
  search_ready: boolean;
  failed: boolean;
  // Terminal-but-not-searchable: e.g. an archive container that finished
  // expanding into member files. Stops polling and suppresses the `· N%`.
  complete?: boolean;
  stages: { key: string; label: string; state: StageState }[];
}

interface DatasetFileRow {
  id: string;
  dataset_id: string;
  filename: string;
  mime: string;
  size_bytes: number;
  // mojo data-plane ingestion states
  status: "queued" | "uploading" | "finishing" | "ready" | "failed";
  created_at: string;
  updated_at?: string | null;
  // Full-pipeline progress through to search-readiness (parse → enrich → index).
  progress?: FileProgress | null;
  search_ready?: boolean;
  browseable?: boolean;
  // Processing error/issue surfaced from the data plane (failed parse / enrich /
  // index, etc.). Shown next to the file when present.
  error_message?: string | null;
}

interface WorkspaceRow {
  id: string;
  name: string;
  slug: string;
}

const TABULAR_EXT = [".csv", ".tsv", ".xlsx", ".xls", ".xlsm", ".xlsb", ".ods"];

function isTabularFile(f: DatasetFileRow): boolean {
  const n = f.filename.toLowerCase();
  return TABULAR_EXT.some((ext) => n.endsWith(ext));
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

const STAGE_TONE: Record<StageState, string> = {
  done: "bg-[var(--color-card-tint-mint)]",
  active: "bg-[var(--color-card-tint-yellow)] animate-pulse",
  failed: "bg-[var(--color-card-tint-rose)]",
  pending: "bg-[var(--color-hairline)]",
};

/** Per-file pipeline progress: a 4-segment bar (Uploaded → Extracted →
 *  Enriched → Searchable) + a state label. Falls back to the raw status badge
 *  before the data plane reports progress. */
function FileProgressCell({ f }: { f: DatasetFileRow }) {
  const p = f.progress;
  if (!p) return <StatusBadge label={f.status} status={f.status} />;
  const tone =
    p.search_ready || p.complete
      ? "text-[#1f5a36]"
      : p.failed
        ? "text-[var(--color-semantic-error)]"
        : "text-muted-foreground";
  return (
    <div className="flex min-w-[150px] flex-col gap-1">
      <div
        className="flex gap-0.5"
        title={p.stages.map((s) => `${s.label}: ${s.state}`).join("\n")}
      >
        {p.stages.map((s) => (
          <span
            key={s.key}
            className={cn("h-1.5 flex-1 rounded-full", STAGE_TONE[s.state])}
          />
        ))}
      </div>
      <span className={cn("text-[11px] leading-tight", tone)}>
        {p.search_ready || p.complete ? "✓ " : ""}
        {p.label}
        {!p.search_ready && !p.failed && !p.complete ? ` · ${p.pct}%` : ""}
      </span>
      {f.error_message ? (
        <span
          className="line-clamp-2 text-[10px] leading-tight text-[var(--color-semantic-error)]"
          title={f.error_message}
        >
          ⚠ {f.error_message}
        </span>
      ) : null}
    </div>
  );
}

/** Sync panel — folders bound to this dataset via a connection. Polls while any
 *  source is actively syncing (mirrors the files-query pattern). Renders nothing
 *  when the dataset has no synced sources. */
function SyncSection({ datasetId }: { datasetId: string }) {
  const api = useApi();
  const qc = useQueryClient();
  // Demo tenants have no connector sources and must not probe the endpoints.
  const isDemo = useIsDemoManage();

  const sources = useQuery({
    queryKey: ["dataset-sources", datasetId],
    enabled: !isDemo,
    refetchInterval: (q) => {
      const data = q.state.data as SourceOut[] | undefined;
      return data?.some((s) => s.status === "syncing") ? 3000 : false;
    },
    queryFn: () =>
      api.request<SourceOut[]>(`/connectors/datasets/${datasetId}/sources`),
  });

  // The workspaces this dataset fans out into (its assignments) + per-workspace
  // file counts — the source syncs the folder once and materialises a private
  // copy in each of these.
  const fanoutWs = useQuery({
    queryKey: ["dataset-sync-workspaces", datasetId],
    enabled: !isDemo,
    refetchInterval: () =>
      (sources.data ?? []).some((s) => s.status === "syncing") ? 3000 : false,
    queryFn: () =>
      api.request<{ id: string; name: string; asset_count: number }[]>(
        `/connectors/datasets/${datasetId}/workspaces`,
      ),
  });

  const patchFreq = useMutation({
    mutationFn: (vars: { id: string; frequency: string }) =>
      api.request<SourceOut>(`/connectors/sources/${vars.id}`, {
        method: "PATCH",
        body: JSON.stringify({ frequency: vars.frequency }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["dataset-sources", datasetId] });
      toast.success("Schedule updated");
    },
    onError: (err: { message?: string }) => toast.error(err.message ?? "Update failed"),
  });

  const invalidateAll = () => {
    void qc.invalidateQueries({ queryKey: ["dataset-sources", datasetId] });
    void qc.invalidateQueries({ queryKey: ["dataset-sync-workspaces", datasetId] });
    void qc.invalidateQueries({ queryKey: ["admin-dataset-files", datasetId] });
  };

  const syncNow = useMutation({
    mutationFn: (id: string) =>
      api.request<SourceOut>(`/connectors/sources/${id}/sync`, {
        method: "POST",
        body: "{}",
      }),
    onSuccess: () => {
      invalidateAll();
      toast.success("Sync started — fanning out to every assigned workspace");
    },
    onError: (err: { message?: string }) => toast.error(err.message ?? "Sync failed"),
  });

  const removeSource = useMutation({
    mutationFn: (id: string) =>
      api.request<void>(`/connectors/sources/${id}`, { method: "DELETE" }, false),
    onSuccess: () => {
      invalidateAll();
      toast.success("Sync removed");
    },
    onError: (err: { message?: string }) => toast.error(err.message ?? "Remove failed"),
  });

  const unassign = useMutation({
    mutationFn: (wsId: string) =>
      api.request<void>(
        `/connectors/datasets/${datasetId}/workspaces/${wsId}`,
        { method: "DELETE" },
        false,
      ),
    onSuccess: () => {
      invalidateAll();
      toast.success("Unassigned from workspace");
    },
    onError: (err: { message?: string }) => toast.error(err.message ?? "Unassign failed"),
  });

  const list = sources.data ?? [];
  const wsList = fanoutWs.data ?? [];
  if (!list.length && !wsList.length) return null;

  return (
    <Section
      title="Sync"
      description="This dataset syncs from a connected folder and fans out to the workspaces below — each keeps its own copy. Syncing (manually or on a schedule) reprocesses the files in every one."
    >
      <div className="flex flex-col gap-2">
        {list.map((s) => (
          <div
            key={s.id}
            className="group flex flex-wrap items-center gap-3 rounded-lg border border-[var(--color-hairline)] bg-card px-4 py-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-medium text-foreground">
                  Synced folder
                </span>
                <StatusBadge label={s.status} tone={sourceStatusTone(s.status)} />
              </div>
              <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                <code
                  className="max-w-[220px] truncate rounded bg-[var(--color-surface-soft)] px-1 py-0.5 font-mono text-[10px]"
                  title={containerRefLabel(s.container_ref)}
                >
                  {containerRefLabel(s.container_ref)}
                </code>
                <span>·</span>
                <span className="tabular-nums">
                  {s.item_count} {s.item_count === 1 ? "item" : "items"}
                </span>
                <span>·</span>
                <span>
                  {s.last_synced_at
                    ? `Synced ${formatDistanceToNow(new Date(s.last_synced_at), { addSuffix: true })}`
                    : "Never synced"}
                </span>
              </p>
              {s.last_error && (
                <p
                  className="mt-1 line-clamp-2 text-[11px] leading-tight text-[var(--color-semantic-error)]"
                  title={s.last_error}
                >
                  ⚠ {s.last_error}
                </p>
              )}
            </div>

            <FrequencySelect
              value={s.frequency}
              disabled={patchFreq.isPending}
              onChange={(frequency) => patchFreq.mutate({ id: s.id, frequency })}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={s.status === "syncing" || syncNow.isPending}
              onClick={() => syncNow.mutate(s.id)}
            >
              <RefreshCw
                className={cn("size-3.5", s.status === "syncing" && "animate-spin")}
              />
              {s.status === "syncing" ? "Syncing…" : "Sync now"}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 opacity-0 transition group-hover:opacity-100"
              aria-label="Remove this synced folder"
              disabled={removeSource.isPending}
              onClick={() => {
                if (
                  confirm(
                    "Remove this synced folder? Every workspace's copy of its files will be removed.",
                  )
                ) {
                  removeSource.mutate(s.id);
                }
              }}
            >
              <Trash2 className="size-3.5 text-muted-foreground" />
            </Button>
          </div>
        ))}
      </div>

      {wsList.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <span className="text-xs text-muted-foreground">Synced into:</span>
          {wsList.map((w) => (
            <span
              key={w.id}
              className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-hairline)] bg-[var(--color-surface-soft)] py-1 pl-2.5 pr-1.5 text-xs"
            >
              <span className="font-medium text-foreground">{w.name}</span>
              <span className="tabular-nums text-muted-foreground">
                · {w.asset_count} {w.asset_count === 1 ? "file" : "files"}
              </span>
              <button
                type="button"
                aria-label={`Unassign from ${w.name}`}
                disabled={unassign.isPending}
                className="ml-0.5 rounded-full px-1 leading-none text-muted-foreground transition hover:bg-card hover:text-[var(--color-semantic-error)] disabled:opacity-50"
                onClick={() => {
                  if (
                    confirm(
                      `Unassign this dataset from "${w.name}"? Its copy of the files there will be removed.`,
                    )
                  ) {
                    unassign.mutate(w.id);
                  }
                }}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
    </Section>
  );
}

function DatasetDetailPage() {
  const api = useApi();
  const navigate = useNavigate();
  // Demo sessions: read-only — no uploads, no file deletes, and the DuckDB
  // browser (tables + sample endpoints) is demo-403'd, so hide its entry too.
  const isDemo = useIsDemoManage();
  const { id: datasetId } = useParams({ from: "/manage/datasets/$id/" });
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [workspaceId, setWorkspaceId] = useState("");

  const dataset = useQuery({
    queryKey: ["admin-dataset", datasetId],
    queryFn: () => api.request<DatasetRead>(`/admin/datasets/${datasetId}`),
  });

  // Workspace to process uploads into. mojo indexes per-workspace, so a file
  // only becomes searchable in the workspace(s) it's processed for — the admin
  // picks which one here (mojo also fans out to any other workspace this
  // dataset is assigned to).
  const orgId = dataset.data?.org_id;
  const workspaces = useQuery({
    queryKey: ["admin-org-workspaces", orgId],
    enabled: Boolean(orgId) && !isDemo,
    queryFn: () =>
      api.request<WorkspaceRow[]>(`/admin/orgs/${orgId}/workspaces`),
  });

  // Default the active workspace to the first one: files are materialised as a
  // private copy per workspace, so the list is scoped to one workspace (a single
  // de-duplicated "files of <workspace>" view) and uploads have a target.
  useEffect(() => {
    const first = workspaces.data?.[0];
    if (!workspaceId && first) {
      setWorkspaceId(first.id);
    }
  }, [workspaceId, workspaces.data]);

  const files = useQuery({
    queryKey: ["admin-dataset-files", datasetId, workspaceId],
    enabled: Boolean(workspaceId),
    refetchInterval: (q) => {
      const data = q.state.data as DatasetFileRow[] | undefined;
      // Keep polling until every file is either fully search-ready or failed —
      // a file can be "ready" (uploaded/parsed) while enrichment + indexing are
      // still in flight.
      const hasPending = data?.some(
        (f) =>
          !f.search_ready &&
          f.status !== "failed" &&
          !f.progress?.failed &&
          // Terminal non-searchable rows (e.g. an expanded archive container)
          // are done — they never become search-ready, so don't poll for them.
          !f.progress?.complete,
      );
      return hasPending ? 3000 : false;
    },
    queryFn: () =>
      api.request<DatasetFileRow[]>(
        `/admin/datasets/${datasetId}/files?workspace_id=${encodeURIComponent(workspaceId)}`,
      ),
  });

  const deleteFile = useMutation({
    mutationFn: (fileId: string) =>
      api.request<void>(
        `/admin/datasets/${datasetId}/files/${fileId}`,
        { method: "DELETE" },
        false,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-dataset-files", datasetId] });
      toast.success("File removed");
    },
  });

  async function uploadOne(file: File): Promise<void> {
    // One-step upload: stream the file to mojo's bronze store and finalize so
    // it parses/indexes into the chosen workspace (replaces the old
    // presign → PUT → finalize dance).
    const form = new FormData();
    form.append("file", file, file.name);
    await api.request<unknown>(
      `/admin/datasets/${datasetId}/files?workspace_id=${encodeURIComponent(workspaceId)}`,
      { method: "POST", body: form },
    );
  }

  async function onPick(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const picked = event.target.files ? Array.from(event.target.files) : [];
    if (!picked.length) return;
    event.target.value = "";
    if (!workspaceId) {
      toast.error("Pick a workspace to process the files into first.");
      return;
    }
    setUploadingCount((c) => c + picked.length);
    for (const f of picked) {
      try {
        await uploadOne(f);
        toast.success(`Uploaded ${f.name}`);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        toast.error(`Failed: ${f.name} — ${msg}`);
      } finally {
        setUploadingCount((c) => Math.max(0, c - 1));
      }
    }
    void qc.invalidateQueries({ queryKey: ["admin-dataset-files", datasetId] });
    void qc.invalidateQueries({ queryKey: ["admin-datasets", dataset.data?.org_id] });
  }

  if (dataset.isLoading || !dataset.data) {
    return (
      <div className="mx-auto max-w-[960px] px-10 py-10">
        <Skeleton className="h-10 w-60" />
        <Skeleton className="mt-8 h-40 w-full" />
      </div>
    );
  }

  const d = dataset.data;
  const selectedWsName = workspaces.data?.find((w) => w.id === workspaceId)?.name;
  const fileCount = files.data?.length ?? 0;
  const tabularCount = files.data?.filter(isTabularFile).length ?? 0;
  const readyCount =
    files.data?.filter((f) => f.search_ready).length ?? 0;
  const totalBytes =
    files.data?.reduce((acc, f) => acc + (f.size_bytes ?? 0), 0) ?? 0;

  return (
    <div className="mx-auto max-w-[960px] px-10 py-10">
      <Link
        to="/manage/datasets"
        className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-3" /> All datasets
      </Link>

      <PageHeader
        eyebrow="Dataset"
        tabTitle={[d.name, "Datasets"]}
        title={
          <span className="inline-flex items-center gap-3">
            <span className="text-3xl">{d.icon ?? "📚"}</span>
            {d.name}
          </span>
        }
        meta={
          <>
            <code className="rounded bg-[var(--color-surface-soft)] px-1.5 py-0.5 font-mono text-[11px]">
              /{d.slug}
            </code>
            <span>· Updated {new Date(d.updated_at).toLocaleDateString()}</span>
          </>
        }
        description={d.description ?? undefined}
        actions={
          isDemo ? undefined : (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.csv,.tsv,.xlsx,.xls,.xlsm,.xlsb,.ods,.docx,.pptx,.html,.htm,.txt,.md,.json,.eml,.msg,.png,.jpg,.jpeg,.webp,.gif,.tif,.tiff,.bmp,.zip"
                className="hidden"
                onChange={onPick}
              />
              <select
                aria-label="Workspace"
                title="Workspace — the files shown below and any uploads apply to this workspace"
                value={workspaceId}
                onChange={(e) => setWorkspaceId(e.target.value)}
                className="h-9 rounded-md border border-[var(--color-hairline)] bg-card px-2.5 text-sm text-foreground"
              >
                {!workspaceId && (
                  <option value="" disabled>
                    Select workspace…
                  </option>
                )}
                {workspaces.data?.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
              {tabularCount > 0 && (
                <Button
                  variant="outline"
                  onClick={() =>
                    navigate({
                      to: "/manage/datasets/$id/data",
                      params: { id: datasetId },
                    })
                  }
                >
                  <Database className="size-4" />
                  Browse data
                </Button>
              )}
              <Button
                disabled={!workspaceId}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="size-4" />
                Upload files
              </Button>
            </>
          )
        }
      />

      <StatStrip
        items={[
          { label: "Files", value: fileCount, hint: "in dataset" },
          {
            label: "Tabular",
            value: tabularCount,
            hint: "go into DuckDB",
          },
          { label: "Search-ready", value: readyCount, hint: "fully processed" },
          { label: "Size", value: fmtBytes(totalBytes), hint: "total bytes" },
        ]}
      />

      <SyncSection datasetId={datasetId} />

      <Section
        title={selectedWsName ? `Files of ${selectedWsName}` : "Files"}
        description={
          uploadingCount > 0
            ? `${uploadingCount} uploading…`
            : "Files this dataset holds in the selected workspace — switch the workspace with the picker above. PDF, CSV, XLSX, DOCX, PPTX; tabular files get ingested into that workspace's DuckDB."
        }
      >
        {files.isLoading ? (
          <Skeleton className="h-32 w-full rounded-md" />
        ) : (
          <div className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-surface-soft)]">
                <tr className="text-left">
                  <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                    Filename
                  </th>
                  <th className="w-32 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                    Type
                  </th>
                  <th className="w-24 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                    Size
                  </th>
                  <th className="w-28 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                    Added
                  </th>
                  <th className="w-28 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                    Updated
                  </th>
                  <th className="w-56 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                    Processing
                  </th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-hairline-soft)]">
                {files.data?.length ? (
                  files.data.map((f) => (
                    <tr
                      key={f.id}
                      className="group transition hover:bg-[var(--color-surface)]"
                    >
                      <td className="break-all px-4 py-2.5">
                        <span className="inline-flex items-center gap-2">
                          {isTabularFile(f) && (
                            <span className="inline-block size-1.5 rounded-full bg-[var(--color-brand-purple)]" />
                          )}
                          {f.search_ready || f.browseable ? (
                            <Link
                              to="/manage/datasets/$id/files/$assetId"
                              params={{ id: datasetId, assetId: f.id }}
                              className="text-foreground underline-offset-2 hover:text-[var(--color-brand-terracotta)] hover:underline"
                              title="Browse extracted content"
                            >
                              {f.filename}
                            </Link>
                          ) : (
                            f.filename
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
                        {f.mime}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs tabular-nums">
                        {fmtBytes(f.size_bytes)}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs tabular-nums text-muted-foreground">
                        {f.created_at
                          ? new Date(f.created_at).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs tabular-nums text-muted-foreground">
                        {f.updated_at
                          ? new Date(f.updated_at).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="px-4 py-2.5">
                        <FileProgressCell f={f} />
                      </td>
                      <td className="px-2 py-2">
                        {!isDemo && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-7 opacity-0 transition group-hover:opacity-100"
                            onClick={() => deleteFile.mutate(f.id)}
                            aria-label="Delete file"
                          >
                            <Trash2 className="size-3.5 text-muted-foreground" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <EmptyRow colSpan={7}>
                    No files. Upload PDF / CSV / XLSX / DOCX / PPTX.
                  </EmptyRow>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
